import classNames from 'classnames'
import { Field, FieldAttributes, getIn, useFormikContext } from 'formik'
import { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react'

export type InputProps = (
  | FieldAttributes<HTMLTextAreaElement>
  | InputHTMLAttributes<HTMLInputElement>
  | TextareaHTMLAttributes<HTMLTextAreaElement>
) & { as?: string }

export const INPUT_STYLE_BASIC = ' box-border w-full min-h-12 border px-6 py-3 '
export const INPUT_STYLE_STANDARD =
  ' softlock-new pl-2 border-borderColor ' +
  'border-solid outline-none pr-4 disabled:border-none transition duration-300 text-sm ' +
  'transition duration-300 '
export const INPUT_STYLE_DEFAULT_TYPOGRAPHY = ' text-xs font-semibold text-secondaryFontColor '
export const INPUT_STYLE_DEFAULT = INPUT_STYLE_BASIC + INPUT_STYLE_DEFAULT_TYPOGRAPHY
export const INPUT_STYLE = INPUT_STYLE_BASIC + INPUT_STYLE_STANDARD

type FormikValidationStatus = {
  showValidationErrors?: boolean
}

export const RequiredMark = () => (
  <span aria-hidden="true" className="text-alertColor ml-1">
    *
  </span>
)

export const RequiredLabel = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => (
  <span className={classNames('inline-flex items-center', className)}>
    {children}
    <RequiredMark />
  </span>
)

export const shouldShowFormikError = (
  formik: {
    errors: unknown
    touched: unknown
    status?: FormikValidationStatus
  },
  name: string
) => {
  return (
    getIn(formik.errors, name) != undefined &&
    (getIn(formik.touched, name) != undefined || formik.status?.showValidationErrors === true)
  )
}

export const FieldErrorMessage = ({
  name,
  className = 'text-alertColor',
}: {
  name: string
  className?: string
}) => {
  const formik = useFormikContext<unknown>()
  const error = getIn(formik.errors, name) as unknown

  if (!shouldShowFormikError(formik, name)) return null

  return <div className={className}>{String(error)}</div>
}

export default function InputField({ className, ...props }: InputProps) {
  const formik = useFormikContext<unknown>()
  return (
    <Field
      {...props}
      className={classNames(INPUT_STYLE, className, {
        '!border-alertColor': shouldShowFormikError(formik, props.name as string),
      })}
    />
  )
}
